import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { toast } from "sonner";
import { Download, FileText, Users, Store, ShoppingCart, Wallet, Gift, AlertTriangle, Cloud, Copy, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REPORT_TYPES = [
  { key: "dealers", label: "Dealers Report", icon: Store, color: "bg-emerald-50 text-emerald-600", desc: "All dealer/party data with contacts, addresses, documents" },
  { key: "employees", label: "Employees Report", icon: Users, color: "bg-blue-50 text-blue-600", desc: "Staff list with personal details, banking info" },
  { key: "orders", label: "Orders Report", icon: ShoppingCart, color: "bg-violet-50 text-violet-600", desc: "All orders with amounts, status, dates" },
  { key: "collections", label: "Collections Report", icon: Wallet, color: "bg-amber-50 text-amber-600", desc: "Payment collections with amounts, modes" },
  { key: "schemes", label: "Schemes Report", icon: Gift, color: "bg-purple-50 text-purple-600", desc: "Active and expired promotional schemes" },
  { key: "grievances", label: "Grievances Report", icon: AlertTriangle, color: "bg-red-50 text-red-600", desc: "Complaints and resolutions" },
];

const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) { toast.error("No data to download"); return; }
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(","),
    ...data.map(row => headers.map(h => {
      let val = row[h];
      if (val === null || val === undefined) val = "";
      if (Array.isArray(val)) val = val.join("; ");
      if (typeof val === "object") val = JSON.stringify(val);
      val = String(val).replace(/"/g, '""');
      return `"${val}"`;
    }).join(","))
  ].join("\n");
  
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
  link.click();
};

const ReportsPage = () => {
  const { user } = useAuth();
  const [region, setRegion] = useState("ALL");
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [saving, setSaving] = useState("");
  const [exports, setExports] = useState([]);

  const fetchSummary = async () => {
    try {
      const res = await authFetch(`${API}/reports/summary?region=${region}`);
      const data = await res.json();
      if (data.success) setSummary(data.data);
    } catch { console.error("Failed to load summary"); }
  };

  const fetchExports = async () => {
    try {
      const res = await authFetch(`${API}/reports/exports?limit=10`);
      const d = await res.json();
      if (d.success) setExports(d.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchSummary(); fetchExports(); }, [region]);

  const handleDownload = async (reportType) => {
    setDownloading(reportType);
    try {
      const res = await authFetch(`${API}/reports/download/${reportType}?region=${region}`);
      const data = await res.json();
      if (data.success) {
        downloadCSV(data.data, reportType);
        toast.success(`${reportType} report downloaded`);
      }
    } catch { toast.error("Download failed"); }
    finally { setDownloading(""); }
  };

  const handleSaveToCloud = async (reportType) => {
    setSaving(reportType);
    try {
      const res = await authFetch(`${API}/reports/save-to-storage/${reportType}?region=${region}`, { method: "POST" });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Uploaded ${data.data.row_count} rows · ${(data.data.size_bytes / 1024).toFixed(1)} KB`);
        fetchExports();
      } else {
        toast.error(data.detail || data.error?.message || "Failed to upload report");
      }
    } catch { toast.error("Upload failed"); }
    finally { setSaving(""); }
  };

  const copyShareLink = (storagePath) => {
    const tok = typeof localStorage !== "undefined" ? localStorage.getItem("session_token") : "";
    const url = `${API}/files/${storagePath}?auth=${tok}`;
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Download detailed reports for analysis</p>
        </div>
        {user?.role === "SUPER_ADMIN" && (
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-40" data-testid="region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Regions</SelectItem>
              <SelectItem value="GJ">Gujarat</SelectItem>
              <SelectItem value="RJ">Rajasthan</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Dealers", value: summary.total_dealers, icon: Store },
            { label: "Total Employees", value: summary.total_employees, icon: Users },
            { label: "Total Orders", value: summary.total_orders, icon: ShoppingCart },
            { label: "Total Collections", value: summary.total_collections, icon: Wallet },
          ].map((s, i) => (
            <Card key={i} className="p-4" data-testid={`summary-${i}`}>
              <div className="flex items-center gap-3">
                <s.icon className="w-5 h-5 text-[#8B1A1A]" />
                <div>
                  <p className="text-2xl font-bold font-mono text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORT_TYPES.map((report) => (
          <Card key={report.key} className="p-5 hover:shadow-lg transition-shadow" data-testid={`report-card-${report.key}`}>
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl ${report.color}`}>
                <report.icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{report.label}</h3>
                <p className="text-xs text-slate-500 mt-1">{report.desc}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 gap-2 mr-2"
                  onClick={() => handleDownload(report.key)}
                  disabled={downloading === report.key}
                  data-testid={`download-${report.key}`}
                >
                  {downloading === report.key ? (
                    <div className="w-3 h-3 spinner" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  Download CSV
                </Button>
                <Button
                  size="sm"
                  className="mt-3 gap-2 bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => handleSaveToCloud(report.key)}
                  disabled={saving === report.key}
                  data-testid={`save-cloud-${report.key}`}
                  title="Upload CSV to cloud storage and get a shareable link"
                >
                  {saving === report.key ? (
                    <div className="w-3 h-3 spinner" />
                  ) : (
                    <Cloud className="w-3.5 h-3.5" />
                  )}
                  Save to Cloud
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Recent cloud exports (P2 iter81) */}
      {exports.length > 0 && (
        <Card className="p-5" data-testid="recent-exports">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
            <Cloud className="w-4 h-4 text-violet-600" />
            Recent cloud exports
            <span className="ml-auto text-xs font-normal text-slate-400">{exports.length} saved</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="exports-table">
              <thead className="text-slate-500">
                <tr className="border-b">
                  <th className="text-left py-1.5 px-2">Report</th>
                  <th className="text-left py-1.5 px-2">Region</th>
                  <th className="text-left py-1.5 px-2">Rows</th>
                  <th className="text-left py-1.5 px-2">Size</th>
                  <th className="text-left py-1.5 px-2">Exported</th>
                  <th className="text-left py-1.5 px-2">By</th>
                  <th className="text-right py-1.5 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {exports.map((e) => (
                  <tr key={e.export_id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-1.5 px-2 font-medium capitalize">{e.report_type}</td>
                    <td className="py-1.5 px-2">{e.region}</td>
                    <td className="py-1.5 px-2 font-mono">{e.row_count}</td>
                    <td className="py-1.5 px-2 font-mono">{(e.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="py-1.5 px-2 text-slate-500">{new Date(e.exported_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="py-1.5 px-2 text-slate-500">{e.exported_by_name || "—"}</td>
                    <td className="py-1.5 px-2 text-right">
                      <button onClick={() => copyShareLink(e.storage_path)} className="text-violet-600 hover:underline mr-2 inline-flex items-center gap-1" data-testid={`copy-link-${e.export_id}`}>
                        <Copy className="w-3 h-3" /> Copy link
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
